import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { WS_CONFIG, WS_STATUS } from '../constants';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children, isCarVisible }) => {
  const [price, setPrice] = useState(null);
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    let reconnectTimeout = null;

    const connectWebSocket = () => {
      if (!isCarVisible) return;
      
      setIsConnecting(true);
      
      try {
        const newWs = new WebSocket(WS_CONFIG.URL);
        wsRef.current = newWs;
        
        newWs.onopen = () => {
          console.log('Connected to WebSocket server');
          setIsConnecting(false);
          setError(null);
        };

        newWs.onmessage = (event) => {
          try {
            const price = parseFloat(event.data);
            if (!isNaN(price)) {
              setPrice(price);
            }
          } catch (err) {
            console.error('Error parsing message:', err);
          }
        };

        newWs.onerror = (error) => {
          console.error('WebSocket error:', error);
          setError('Failed to connect to price feed');
          setIsConnecting(false);
        };

        newWs.onclose = (event) => {
          console.log('Disconnected from WebSocket server:', event.code, event.reason);
          setIsConnecting(false);
          
          // Handle different close codes
          switch (event.code) {
            case WS_STATUS.NORMAL_CLOSURE:
              // Normal closure, no need to show error
              break;
            case WS_STATUS.GOING_AWAY:
              setError('Server is shutting down');
              break;
            case WS_STATUS.PROTOCOL_ERROR:
              setError('Connection protocol error');
              break;
            case WS_STATUS.POLICY_VIOLATION:
              setError('Authentication failed');
              break;
            case WS_STATUS.SERVICE_RESTART:
              setError('Server is restarting');
              reconnectTimeout = setTimeout(connectWebSocket, 5000);
              break;
            case WS_STATUS.TRY_AGAIN_LATER:
              setError('Server is temporarily unavailable');
              reconnectTimeout = setTimeout(connectWebSocket, 10000);
              break;
            default:
              setError('Connection lost');
              // Try to reconnect for unknown errors
              reconnectTimeout = setTimeout(connectWebSocket, 3000);
          }
        };
      } catch (err) {
        console.error('Error creating WebSocket:', err);
        setError('Failed to initialize connection');
        setIsConnecting(false);
      }
    };

    if (isCarVisible) {
      connectWebSocket();
    } else {
      // Close connection if car is not visible
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [isCarVisible]);

  return (
    <WebSocketContext.Provider value={{ price, error, isConnecting }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}; 